import type {
  PrintLayoutConfig,
  StorePrinter,
} from "@medicorepay/shared/types";
import { INVOICE_REPRINT_AUDIT_REASON } from "@medicorepay/shared";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { openInvoicePrintWindow } from "../../lib/revival-invoice-print";
import { showToast } from "../../lib/toast";
import { formatDateTime } from "../../lib/utils";
import { Invoice, invoiceService } from "../../services/invoice.service";
import { printerDispatchService } from "../../services/printer-dispatch.service";
import { printerService } from "../../services/printer.service";
import { useAuthStore } from "../../stores/authStore";

function requestErrorMessage(error: unknown, fallback: string) {
  const candidate = error as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return candidate.response?.data?.message || candidate.message || fallback;
}

export function InvoiceViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const selectedStoreId = useAuthStore((s) => s.selectedStoreId);
  const userStoreId = useAuthStore((s) => s.user?.storeId);
  const currentStoreId = selectedStoreId || userStoreId;
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState("");
  const [printerConfig, setPrinterConfig] = useState<StorePrinter | null>(null);

  useEffect(() => {
    if (id) {
      loadInvoice(id);
    }
  }, [id]);

  // Load default billing printer config for the current store
  useEffect(() => {
    let cancelled = false;
    setPrinterConfig(null);
    if (currentStoreId) {
      printerService
        .getDefaultForStore(currentStoreId, "BILLING")
        .then((config) => {
          if (!cancelled) setPrinterConfig(config);
        })
        .catch(() => {}); // Silently fail — use default print
    }
    return () => {
      cancelled = true;
    };
  }, [currentStoreId]);

  // Generate dynamic print CSS based on printer layout config
  const dynamicPrintStyle = useMemo(() => {
    const layout = printerConfig?.layoutConfig as PrintLayoutConfig | null;

    // Common base: isolate the print area
    const basePrint = `
      body * { visibility: hidden; }
      #invoice-print-area, #invoice-print-area * { visibility: visible; }
      #invoice-print-area { position: absolute; left: 0; top: 0; }
      .print\\:hidden { display: none !important; }
    `;

    if (!layout) {
      // Default A4-like print
      return `
        @media print {
          @page { size: A4; margin: 10mm; }
          body { -webkit-print-color-adjust: exact; }
          ${basePrint}
          #invoice-print-area { width: 100%; }
          #invoice-print-area table { min-width: 0 !important; }
        }
      `;
    }

    const isThermal =
      printerConfig?.printerType === "THERMAL_58MM" ||
      printerConfig?.printerType === "THERMAL_80MM";
    const isDotMatrix =
      printerConfig?.printerType === "DOT_MATRIX" ||
      printerConfig?.printerType === "DOT_MATRIX_9_5X5_5";
    const isCompact = isThermal || (isDotMatrix && layout.compactMode);
    const paperW = printerConfig?.paperWidth || 210;
    const paperH = printerConfig?.paperHeight;
    const m = layout.margins || { top: 5, right: 5, bottom: 5, left: 5 };

    // Section/column visibility rules
    const hideRules = [
      !layout.showHeader &&
        "#invoice-print-area .print-section-header { display: none !important; }",
      !layout.showFooter &&
        "#invoice-print-area .print-section-footer { display: none !important; }",
      !layout.showDoctor &&
        "#invoice-print-area .print-section-doctor { display: none !important; }",
      !layout.showHSN &&
        "#invoice-print-area .print-col-hsn { display: none !important; }",
      !layout.showBatch &&
        "#invoice-print-area .print-col-batch { display: none !important; }",
      !layout.showMfr &&
        "#invoice-print-area .print-col-mfr { display: none !important; }",
      !layout.showSchedule &&
        "#invoice-print-area .print-col-schedule { display: none !important; }",
      !layout.showMRP &&
        "#invoice-print-area .print-col-mrp { display: none !important; }",
      !layout.showExpiry &&
        "#invoice-print-area .print-col-expiry { display: none !important; }",
      !layout.showDiscount &&
        "#invoice-print-area .print-col-discount { display: none !important; }",
      !layout.showTaxBreakdown &&
        "#invoice-print-area .print-section-tax-breakdown { display: none !important; }",
      !layout.showPatientInfo &&
        "#invoice-print-area .print-section-patient { display: none !important; }",
      !layout.showGST &&
        "#invoice-print-area .print-section-gst { display: none !important; }",
      !layout.showLogo &&
        "#invoice-print-area .print-logo { display: none !important; }",
    ]
      .filter(Boolean)
      .join("\n        ");

    // Compact mode rules — for thermal AND dot-matrix with compactMode
    const compactRules = isCompact
      ? `
        #invoice-print-area .print-thermal-hide { display: none !important; }
        #invoice-print-area h1 { font-size: ${layout.fontSize + 2}pt !important; margin: 0 !important; }
        #invoice-print-area h2 { font-size: ${layout.fontSize + 1}pt !important; margin: 0 !important; }
        #invoice-print-area h3, #invoice-print-area h4 { font-size: ${layout.fontSize}pt !important; margin: 0 !important; }
        #invoice-print-area table { font-size: ${Math.max(layout.fontSize - 1, 6)}pt !important; }
        #invoice-print-area .print-compact-padding { padding: 1px 0 !important; }
        #invoice-print-area p { margin: 0 !important; }
        #invoice-print-area hr { margin: 2px 0 !important; }
        #invoice-print-area .mb-8, #invoice-print-area .mb-10 { margin-bottom: 4px !important; }
        #invoice-print-area .mb-6 { margin-bottom: 3px !important; }
        #invoice-print-area .p-8, #invoice-print-area .md\\:p-12 { padding: 4mm !important; }
        #invoice-print-area .p-5 { padding: 2mm !important; }
        #invoice-print-area .py-3 { padding-top: 1px !important; padding-bottom: 1px !important; }
        #invoice-print-area .gap-6 { gap: 2mm !important; }
        #invoice-print-area .gap-8 { gap: 3mm !important; }
        #invoice-print-area .rounded-2xl, #invoice-print-area .rounded-lg { border-radius: 0 !important; }
        #invoice-print-area .shadow-xl { box-shadow: none !important; }
        #invoice-print-area .bg-gray-50, #invoice-print-area .bg-yellow-50,
        #invoice-print-area .bg-blue-50, #invoice-print-area .bg-emerald-50,
        #invoice-print-area .bg-orange-50, #invoice-print-area .bg-red-50,
        #invoice-print-area .bg-amber-50 { background: none !important; }
        #invoice-print-area .border { border: none !important; }
        #invoice-print-area .border-gray-100, #invoice-print-area .border-gray-200 { border-color: #ccc !important; }
      `
      : "";

    return `
      @media print {
        @page {
          size: ${paperW}mm ${paperH ? `${paperH}mm` : "auto"};
          margin: ${m.top}mm ${m.right}mm ${m.bottom}mm ${m.left}mm;
        }
        body { -webkit-print-color-adjust: exact; margin: 0; padding: 0; }
        html, body { height: auto; }
        ${basePrint}
        #invoice-print-area {
          width: ${paperW - m.left - m.right}mm !important;
          max-width: ${paperW - m.left - m.right}mm !important;
          font-family: ${layout.fontFamily || "monospace"}, monospace;
          font-size: ${layout.fontSize}pt;
          line-height: ${layout.lineSpacing};
          padding: 0;
          overflow: hidden;
        }
        #invoice-print-area table {
          min-width: 0 !important;
          width: 100% !important;
          table-layout: auto;
        }
        #invoice-print-area .max-w-4xl { max-width: none !important; }
        ${compactRules}
        ${hideRules}
      }
    `;
  }, [printerConfig]);

  // Inject print styles into <head> so they work correctly with @media print
  useEffect(() => {
    const styleEl = document.createElement("style");
    styleEl.setAttribute("data-print-config", "true");
    styleEl.textContent = dynamicPrintStyle;
    document.head.appendChild(styleEl);
    return () => {
      document.head.removeChild(styleEl);
    };
  }, [dynamicPrintStyle]);

  const loadInvoice = async (invoiceId: string) => {
    try {
      setLoading(true);
      const data = await invoiceService.getInvoice(invoiceId);
      setInvoice(data);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load invoice");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    if (!invoice || printing) return;
    if (!currentStoreId) {
      showToast.error("Select a store before printing");
      return;
    }

    let printWindow: Window | null = null;
    try {
      printWindow = openInvoicePrintWindow();
    } catch (error) {
      showToast.error(
        requestErrorMessage(error, "Allow the print window and retry"),
      );
      return;
    }

    setPrinting(true);
    try {
      const printers = await printerService.getByStore(currentStoreId);
      const defaultPrinter = printers.find(
        (candidate) =>
          candidate.isActive &&
          candidate.isDefault &&
          candidate.purpose === "BILLING",
      );
      const printer =
        (defaultPrinter?.connectionType === "SYSTEM"
          ? defaultPrinter
          : undefined) ??
        printers.find(
          (candidate) =>
            candidate.isActive &&
            candidate.purpose === "BILLING" &&
            candidate.connectionType === "SYSTEM",
        );
      if (!printer) {
        throw new Error(
          "Configure a system billing printer for web printing, or use MediCorePay Desktop",
        );
      }

      await printerDispatchService.createAndDispatchInvoice({
        saleId: invoice.id,
        printer,
        reason: INVOICE_REPRINT_AUDIT_REASON,
        printWindow,
      });
      showToast.success(
        `Invoice ${invoice.invoiceNumber} opened with ${printer.name}`,
      );
    } catch (error) {
      if (printWindow && !printWindow.closed) printWindow.close();
      showToast.error(requestErrorMessage(error, "Unable to print invoice"));
    } finally {
      setPrinting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-gray-500">Loading invoice...</div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error || "Invoice not found"}
        </div>
        <button
          onClick={() => navigate("/pos")}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Back to POS
        </button>
      </div>
    );
  }

  const isBillOfSupply = !!invoice.tenant?.isCompositeDealer;
  const qrCodeDataUrl =
    typeof invoice.signedQrCode === "string" &&
    invoice.signedQrCode.startsWith("data:image")
      ? invoice.signedQrCode
      : null;

  return (
    <div className="min-h-screen bg-gray-50 py-8 print:bg-white print:py-0">
      <div className="max-w-4xl mx-auto print:w-full">
        {/* Actions - specific to screen */}
        <div className="mb-6 flex items-center justify-between print:hidden px-4 md:px-0">
          <button
            onClick={() => navigate("/pos")}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            ← Back to POS
          </button>
          <div className="flex gap-3">
            <button
              onClick={() =>
                navigate(
                  `/sales-returns/create?invoiceNumber=${encodeURIComponent(invoice.invoiceNumber)}`,
                )
              }
              className="px-5 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium"
            >
              Create Return
            </button>
            <button
              onClick={handlePrint}
              disabled={printing}
              className="px-5 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors flex items-center gap-2 font-medium disabled:cursor-wait disabled:opacity-60"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
              {printing ? "Preparing…" : "Print Invoice"}
            </button>
            <button
              onClick={() => navigate("/pos")}
              className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              New Sale
            </button>
          </div>
        </div>

        {/* Invoice Card */}
        <div
          id="invoice-print-area"
          className="bg-white shadow-xl rounded-2xl overflow-hidden print:shadow-none print:rounded-none"
        >
          {/* Top Border/Brand Color */}
          <div className="h-2 bg-gradient-to-r from-blue-600 to-emerald-500 print:hidden"></div>

          <div className="p-8 md:p-12 print:p-8">
            {/* Header Section */}
            <div className="print-section-header flex flex-col md:flex-row justify-between items-start mb-8 gap-6">
              {/* Left: Pharmacy/Store Details */}
              <div>
                <h1 className="text-2xl font-bold text-slate-900 mb-1">
                  {invoice.store?.name || "Pharmacy Store"}
                </h1>

                <div className="text-sm text-gray-600 leading-relaxed mt-3 space-y-1">
                  {invoice.store?.address && <p>{invoice.store.address}</p>}
                  {(invoice.store?.city || invoice.store?.state) && (
                    <p>
                      {[
                        invoice.store.city,
                        invoice.store.state,
                        invoice.store.pincode,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  )}
                  {invoice.store?.phone && (
                    <p>
                      <span className="font-medium text-gray-700">Tel:</span>{" "}
                      {invoice.store.phone}
                    </p>
                  )}
                  <div className="print-section-gst">
                    {invoice.store?.gstNumber && (
                      <p>
                        <span className="font-medium text-gray-700">
                          GSTIN:
                        </span>{" "}
                        <span className="font-mono">
                          {invoice.store.gstNumber}
                        </span>
                      </p>
                    )}
                    {invoice.store?.drugLicenses?.[0] && (
                      <p>
                        <span className="font-medium text-gray-700">
                          DL No:
                        </span>{" "}
                        <span className="font-mono">
                          {invoice.store.drugLicenses[0].licenseNumber}
                        </span>
                      </p>
                    )}
                    {invoice.store?.fssaiRegistrations?.[0] && (
                      <p>
                        <span className="font-medium text-gray-700">
                          FSSAI:
                        </span>{" "}
                        <span className="font-mono">
                          {invoice.store.fssaiRegistrations[0].fssaiNumber}
                        </span>
                      </p>
                    )}
                    {invoice.tenant?.cinNumber && (
                      <p>
                        <span className="font-medium text-gray-700">CIN:</span>{" "}
                        <span className="font-mono">
                          {invoice.tenant.cinNumber}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: Invoice Details */}
              <div className="text-left md:text-right">
                <div className="inline-block bg-blue-50 text-blue-700 font-bold px-4 py-1.5 rounded-full text-sm mb-4 border border-blue-100">
                  {isBillOfSupply ? "BILL OF SUPPLY" : "TAX INVOICE"}
                </div>
                <h2 className="text-xl font-mono font-semibold text-slate-900 mb-1">
                  #{invoice.invoiceNumber}
                </h2>
                <p className="text-gray-500 text-sm mb-4">
                  {formatDateTime(invoice.invoiceDate)}
                </p>

                <div className="inline-flex flex-col items-start md:items-end gap-1">
                  {invoice.isReverseCharge && (
                    <span className="text-violet-700 font-bold border border-violet-200 bg-violet-50 px-3 py-1 rounded text-xs">
                      REVERSE CHARGE
                    </span>
                  )}
                  {invoice.paymentStatus === "PAID" ? (
                    <span className="text-emerald-600 font-bold border border-emerald-200 bg-emerald-50 px-3 py-1 rounded text-xs">
                      PAID
                    </span>
                  ) : invoice.paymentStatus === "PARTIAL" ? (
                    <span className="text-amber-600 font-bold border border-amber-200 bg-amber-50 px-3 py-1 rounded text-xs">
                      PARTIAL
                    </span>
                  ) : (
                    <span className="text-red-600 font-bold border border-red-200 bg-red-50 px-3 py-1 rounded text-xs">
                      UNPAID
                    </span>
                  )}
                </div>
              </div>
            </div>

            {(isBillOfSupply ||
              invoice.isReverseCharge ||
              invoice.irn ||
              invoice.irnStatus ||
              qrCodeDataUrl ||
              invoice.eInvoiceError) && (
              <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Compliance Summary
                  </h3>
                  <div className="mt-3 space-y-2 text-sm text-slate-700">
                    <div>
                      <span className="font-medium text-slate-900">
                        Document Type:
                      </span>{" "}
                      {isBillOfSupply ? "Bill of Supply" : "Tax Invoice"}
                    </div>
                    <div>
                      <span className="font-medium text-slate-900">
                        GST Mode:
                      </span>{" "}
                      {isBillOfSupply
                        ? "Composition dealer supply"
                        : invoice.isReverseCharge
                          ? "Reverse charge"
                          : "Forward charge"}
                    </div>
                    {invoice.reverseChargeReason && (
                      <div>
                        <span className="font-medium text-slate-900">
                          Reason:
                        </span>{" "}
                        {invoice.reverseChargeReason}
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    E-Invoice
                  </h3>
                  <div className="mt-3 space-y-2 text-sm text-slate-700">
                    <div>
                      <span className="font-medium text-slate-900">
                        Status:
                      </span>{" "}
                      {invoice.irnStatus || "NOT_APPLICABLE"}
                    </div>
                    {invoice.irn && (
                      <div>
                        <span className="font-medium text-slate-900">IRN:</span>{" "}
                        <span className="break-all font-mono text-xs">
                          {invoice.irn}
                        </span>
                      </div>
                    )}
                    {invoice.irpAckNumber && (
                      <div>
                        <span className="font-medium text-slate-900">
                          Ack No:
                        </span>{" "}
                        {invoice.irpAckNumber}
                      </div>
                    )}
                    {invoice.irpAckDate && (
                      <div>
                        <span className="font-medium text-slate-900">
                          Ack Date:
                        </span>{" "}
                        {formatDateTime(invoice.irpAckDate)}
                      </div>
                    )}
                    {invoice.eInvoiceError && (
                      <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
                        {invoice.eInvoiceError}
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Signed QR
                  </h3>
                  {qrCodeDataUrl ? (
                    <div className="mt-3 flex justify-center rounded-lg border border-slate-200 bg-white p-3">
                      <img
                        src={qrCodeDataUrl}
                        alt="Signed invoice QR"
                        className="h-32 w-32 object-contain"
                      />
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-slate-500">
                      No signed QR code is stored for this invoice.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Divider */}
            <hr className="border-gray-100 mb-8" />

            {/* Customer + Doctor Section */}
            <div className="mb-10 flex flex-col md:flex-row gap-6">
              {/* Bill To */}
              <div className="print-section-patient flex-1">
                <h3 className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-3">
                  Bill To
                </h3>
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-100">
                  {invoice.customer ? (
                    <div className="text-sm space-y-1">
                      <p className="font-bold text-slate-900 text-lg mb-1">
                        {invoice.customer.name}
                      </p>
                      <p className="text-gray-600">
                        {invoice.customer.phone || "No phone provided"}
                      </p>
                      {(invoice.customer.address || invoice.customer.city) && (
                        <p className="text-gray-500 text-xs">
                          {[
                            invoice.customer.address,
                            invoice.customer.city,
                            invoice.customer.state,
                            invoice.customer.pincode,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                      )}
                      {invoice.customer.loyaltyPoints != null &&
                        invoice.customer.loyaltyPoints > 0 && (
                          <p className="text-xs text-blue-600 mt-2">
                            <span className="font-medium">Loyalty Points:</span>{" "}
                            {invoice.customer.loyaltyPoints}
                          </p>
                        )}
                    </div>
                  ) : (
                    <div className="text-sm">
                      <p className="font-bold text-slate-900 text-lg">
                        Walk-in Customer
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Doctor Info */}
              {invoice.doctor && (
                <div className="print-section-doctor flex-1">
                  <h3 className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-3">
                    Prescribed By
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-5 border border-gray-100">
                    <div className="text-sm space-y-1">
                      <p className="font-bold text-slate-900 text-lg mb-1">
                        Dr. {invoice.doctor.doctorName}
                      </p>
                      <p className="text-gray-600 text-xs">
                        Reg No:{" "}
                        <span className="font-mono">
                          {invoice.doctor.registrationNumber}
                        </span>
                      </p>
                      {invoice.doctor.qualifications && (
                        <p className="text-gray-500 text-xs">
                          {invoice.doctor.qualifications}
                          {invoice.doctor.specialization &&
                            ` — ${invoice.doctor.specialization}`}
                        </p>
                      )}
                      {invoice.doctor.clinicName && (
                        <p className="text-gray-500 text-xs">
                          {invoice.doctor.clinicName}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Items Table */}
            <div className="mb-8 overflow-x-auto">
              <table className="w-full min-w-[900px] print:min-w-0">
                <thead>
                  <tr className="border-b-2 border-slate-100">
                    <th className="text-left py-3 pl-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-8">
                      #
                    </th>
                    <th className="text-left py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                      Item Details
                    </th>
                    <th className="print-col-hsn text-left py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-20">
                      HSN
                    </th>
                    <th className="print-col-mfr text-left py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-16">
                      Mfr
                    </th>
                    <th className="print-col-batch text-center py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-20">
                      Batch
                    </th>
                    <th className="print-col-expiry text-center py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-20">
                      Expiry
                    </th>
                    <th className="print-col-schedule text-center py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-10">
                      Sch
                    </th>
                    <th className="text-right py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-14">
                      Qty
                    </th>
                    <th className="text-right py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-14">
                      Free
                    </th>
                    <th className="print-col-mrp text-right py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-16">
                      MRP
                    </th>
                    <th className="text-right py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-16">
                      Rate
                    </th>
                    <th className="print-col-discount text-right py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-16">
                      Disc
                    </th>
                    <th className="print-col-tax text-right py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-14">
                      CGST%
                    </th>
                    <th className="print-col-tax text-right py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-14">
                      SGST%
                    </th>
                    <th className="text-right py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-20">
                      Taxable
                    </th>
                    <th className="text-right py-3 pr-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-20">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {invoice.items.map((item, index) => {
                    const scheduleLabel =
                      item.product?.scheduleType === "SCHEDULE_H"
                        ? "H"
                        : item.product?.scheduleType === "SCHEDULE_H1"
                          ? "H1"
                          : item.product?.scheduleType === "SCHEDULE_X"
                            ? "X"
                            : item.product?.scheduleType === "GENERAL"
                              ? "-"
                              : "-";

                    const mfrName =
                      item.product?.manufacturerRef?.shortName ||
                      item.product?.manufacturerRef?.manufacturerName ||
                      item.product?.manufacturer ||
                      "-";

                    const lots =
                      item.lots && item.lots.length > 0
                        ? item.lots
                        : item.batch
                          ? [item.batch]
                          : [];
                    const batchLabel = lots.length
                      ? lots.map((lot) => lot.batchNumber).join(", ")
                      : "-";
                    const expiryDate = lots.length
                      ? lots
                          .map((lot) =>
                            lot.expiryDate
                              ? new Date(lot.expiryDate).toLocaleDateString(
                                  "en-IN",
                                  {
                                    month: "short",
                                    year: "2-digit",
                                    timeZone: "Asia/Kolkata",
                                  },
                                )
                              : "-",
                          )
                          .join(", ")
                      : "-";

                    const mrpValue = item.mrp ?? item.batch?.mrp ?? null;

                    const taxableAmt =
                      item.taxableAmount ??
                      Number(item.amount) - Number(item.gstAmount);

                    return (
                      <tr
                        key={item.id}
                        className="group hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="py-3 pl-3 text-xs text-gray-400 font-medium">
                          {index + 1}
                        </td>
                        <td className="py-3 text-xs">
                          <p className="font-semibold text-slate-900">
                            {item.product?.name || "Unknown Product"}
                          </p>
                          <p className="text-[10px] text-gray-400 mt-0.5">
                            {item.product?.sku}
                          </p>
                        </td>
                        <td className="print-col-hsn py-3 text-xs text-gray-500 font-mono">
                          {item.hsnCode || item.product?.hsnCode || "-"}
                        </td>
                        <td
                          className="print-col-mfr py-3 text-xs text-gray-500 truncate max-w-[60px]"
                          title={mfrName}
                        >
                          {mfrName}
                        </td>
                        <td className="print-col-batch py-3 text-center text-xs text-gray-500 font-mono">
                          {batchLabel}
                        </td>
                        <td className="print-col-expiry py-3 text-center text-xs text-gray-500">
                          {expiryDate}
                        </td>
                        <td className="print-col-schedule py-3 text-center">
                          {scheduleLabel !== "-" ? (
                            <span className="inline-block text-[10px] font-bold text-orange-700 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5">
                              {scheduleLabel}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-300">-</span>
                          )}
                        </td>
                        <td className="py-3 text-right text-xs font-medium text-slate-700">
                          {item.quantity}
                        </td>
                        <td className="py-3 text-right text-xs font-medium text-emerald-700">
                          {item.freeQuantity || 0}
                        </td>
                        <td className="print-col-mrp py-3 text-right text-xs text-gray-500">
                          {mrpValue != null
                            ? `₹${Number(mrpValue).toFixed(2)}`
                            : "-"}
                        </td>
                        <td className="py-3 text-right text-xs text-gray-600">
                          ₹{Number(item.rate).toFixed(2)}
                        </td>
                        <td className="print-col-discount py-3 text-right text-xs text-emerald-600 font-medium">
                          {item.discount && Number(item.discount) > 0
                            ? `₹${Number(item.discount).toFixed(2)}`
                            : "-"}
                        </td>
                        <td className="print-col-tax py-3 text-right text-xs text-gray-500">
                          {item.cgstPercent != null
                            ? `${Number(item.cgstPercent).toFixed(1)}%`
                            : `${(Number(item.gstPercent) / 2).toFixed(1)}%`}
                        </td>
                        <td className="print-col-tax py-3 text-right text-xs text-gray-500">
                          {item.sgstPercent != null
                            ? `${Number(item.sgstPercent).toFixed(1)}%`
                            : `${(Number(item.gstPercent) / 2).toFixed(1)}%`}
                        </td>
                        <td className="py-3 text-right text-xs text-gray-600">
                          ₹{Number(taxableAmt).toFixed(2)}
                        </td>
                        <td className="py-3 pr-3 text-right text-xs font-bold text-slate-900">
                          ₹{Number(item.amount).toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer / Totals */}
            <div className="flex flex-col md:flex-row justify-between items-start gap-8 border-t border-gray-100 pt-8">
              {/* Notes & Terms */}
              <div className="w-full md:w-1/2">
                {invoice.notes && (
                  <div className="mb-6">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Notes
                    </h4>
                    <p className="text-sm text-gray-600 bg-yellow-50 p-3 rounded border border-yellow-100 italic">
                      {invoice.notes}
                    </p>
                  </div>
                )}

                {/* Registered Office */}
                {invoice.tenant?.address && (
                  <div className="text-[10px] text-gray-400 mt-4 leading-relaxed">
                    <p className="font-semibold text-gray-500">
                      Registered Office:
                    </p>
                    <p>
                      {[
                        invoice.tenant.address,
                        invoice.tenant.city,
                        invoice.tenant.state,
                        invoice.tenant.pincode,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                    {invoice.tenant.gstNumber && (
                      <p>Company GSTIN: {invoice.tenant.gstNumber}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Calculations */}
              <div className="w-full md:w-2/5">
                <div className="space-y-2.5">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Taxable Amount</span>
                    <span className="font-medium">
                      ₹{Number(invoice.subtotal).toFixed(2)}
                    </span>
                  </div>

                  {/* GST Breakdown */}
                  <div className="print-section-tax-breakdown">
                    {isBillOfSupply ? (
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>GST</span>
                        <span className="font-medium">
                          Not charged under composition scheme
                        </span>
                      </div>
                    ) : invoice.igstAmount != null &&
                      Number(invoice.igstAmount) > 0 ? (
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>IGST</span>
                        <span className="font-medium">
                          ₹{Number(invoice.igstAmount).toFixed(2)}
                        </span>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>CGST</span>
                          <span className="font-medium">
                            ₹
                            {Number(
                              invoice.cgstAmount ??
                                Number(invoice.gstAmount) / 2,
                            ).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>SGST</span>
                          <span className="font-medium">
                            ₹
                            {Number(
                              invoice.sgstAmount ??
                                Number(invoice.gstAmount) / 2,
                            ).toFixed(2)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Item-level discounts total (batch + scheme discounts) */}
                  {invoice.items?.some(
                    (i) => i.discount && Number(i.discount) > 0,
                  ) && (
                    <div className="flex justify-between text-sm text-emerald-600 print-col-discount">
                      <span>Item Discounts</span>
                      <span className="font-medium">
                        -₹
                        {invoice.items
                          .reduce(
                            (sum, i) => sum + (Number(i.discount) || 0),
                            0,
                          )
                          .toFixed(2)}
                      </span>
                    </div>
                  )}

                  {invoice.discountAmount &&
                    Number(invoice.discountAmount) > 0 && (
                      <div className="flex justify-between text-sm text-emerald-600">
                        <span>Discount</span>
                        <span className="font-medium">
                          -₹{Number(invoice.discountAmount).toFixed(2)}
                        </span>
                      </div>
                    )}

                  {invoice.roundOffAmount != null &&
                    Number(invoice.roundOffAmount) !== 0 && (
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>Round Off</span>
                        <span className="font-medium">
                          {Number(invoice.roundOffAmount) >= 0 ? "+" : ""}₹
                          {Number(invoice.roundOffAmount).toFixed(2)}
                        </span>
                      </div>
                    )}

                  {invoice.isReverseCharge && (
                    <div className="flex justify-between text-xs text-violet-600">
                      <span>Reverse Charge</span>
                      <span className="font-semibold">Applicable</span>
                    </div>
                  )}

                  <div className="border-t border-dashed border-gray-200 my-3"></div>

                  <div className="flex justify-between items-end">
                    <span className="text-base font-bold text-slate-900">
                      Net Total
                    </span>
                    <span className="text-2xl font-bold text-blue-600">
                      ₹{Number(invoice.totalAmount).toFixed(2)}
                    </span>
                  </div>

                  <div className="pt-4 mt-4 border-t border-gray-100">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Payment Method</span>
                      <span className="font-semibold text-slate-700 uppercase">
                        {invoice.paymentMethod || "CASH"}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Cashier</span>
                      <span className="font-medium text-slate-700">
                        {invoice.createdBy?.firstName}{" "}
                        {invoice.createdBy?.lastName}
                      </span>
                    </div>
                    {(invoice.loyaltyPointsEarned ?? 0) > 0 && (
                      <div className="flex justify-between text-xs text-blue-600 mt-2">
                        <span>Loyalty Points Earned</span>
                        <span className="font-semibold">
                          +{invoice.loyaltyPointsEarned}
                        </span>
                      </div>
                    )}
                    {(invoice.loyaltyPointsRedeemed ?? 0) > 0 && (
                      <div className="flex justify-between text-xs text-purple-600">
                        <span>Loyalty Points Redeemed</span>
                        <span className="font-semibold">
                          -{invoice.loyaltyPointsRedeemed}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Brand Bar */}
          <div className="print-section-footer bg-slate-50 border-t border-slate-100 p-4 text-center text-xs text-gray-400 print:hidden">
            Thank you for your business!
          </div>
        </div>
      </div>
    </div>
  );
}
